import React, { useState, useEffect } from 'react';
import { billingAPI } from '../services/api';
import colors, { fonts, shadows } from '../theme';

export default function BillingPage() {
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [upgrading, setUpgrading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        try {
            const response = await billingAPI.getSubscription();
            setSubscription(response.data.subscription);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching subscription:', err);
            setError('Failed to load subscription details');
            setLoading(false);
        }
    };

    const handleUpgrade = async (billingPeriod) => {
        setUpgrading(true);
        setError('');

        try {
            // Get current user count from localStorage
            const org = JSON.parse(localStorage.getItem('organization') || '{}');
            const userCount = org.current_users || 6; // Default to 6 for per-user plan

            const response = await billingAPI.createCheckoutSession({
                userCount,
                billingPeriod
            });

            // Redirect to Stripe Checkout
            window.location.href = response.data.url;
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError(err.response?.data?.error || 'Failed to start checkout');
            setUpgrading(false);
        }
    };

    const handleManageBilling = async () => {
        setUpgrading(true);
        setError('');

        try {
            const response = await billingAPI.createPortalSession();
            // Redirect to Stripe Customer Portal
            window.location.href = response.data.url;
        } catch (err) {
            console.error('Error creating portal session:', err);
            setError(err.response?.data?.error || 'Failed to open billing portal');
            setUpgrading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '48px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', color: colors.gray[600] }}>Loading billing information...</div>
            </div>
        );
    }

    const isFreePlan = !subscription?.status || subscription?.status === 'free';
    const isPaidPlan = subscription?.status === 'active';

    return (
        <div style={{ padding: '48px', maxWidth: '1200px', margin: '0 auto', fontFamily: fonts.landing }}>
            <h1 style={{
                fontSize: '36px',
                fontWeight: 800,
                marginBottom: '12px',
                background: colors.gradients.coral,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
            }}>
                Billing & Subscription
            </h1>
            <p style={{ fontSize: '18px', color: colors.gray[600], marginBottom: '48px' }}>
                Manage your subscription and billing settings
            </p>

            {error && (
                <div style={{
                    padding: '16px',
                    background: colors.red[50],
                    color: colors.red[700],
                    borderRadius: '12px',
                    marginBottom: '24px',
                    fontSize: '16px'
                }}>
                    {error}
                </div>
            )}

            {/* Current Plan Card */}
            <div style={{
                background: colors.white,
                borderRadius: '24px',
                padding: '48px',
                boxShadow: shadows.md,
                marginBottom: '32px',
            }}>
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    marginBottom: '24px',
                    color: colors.gray[900],
                }}>
                    Current Plan
                </h2>

                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '32px' }}>
                    <div>
                        <div style={{
                            fontSize: '48px',
                            fontWeight: 800,
                            color: colors.landing.coral,
                            lineHeight: 1,
                        }}>
                            {subscription?.planType === 'free' ? 'Free' :
                             subscription?.planType === 'per_user' ? 'Pro' : 'Enterprise'}
                        </div>
                        <div style={{ fontSize: '16px', color: colors.gray[600], marginTop: '8px' }}>
                            {isPaidPlan && (
                                <>
                                    {subscription?.billingPeriod === 'monthly' ? 'Billed Monthly' : 'Billed Annually'}
                                    {subscription?.currentPeriodEnd && (
                                        <span> • Renews {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</span>
                                    )}
                                </>
                            )}
                            {isFreePlan && 'Up to 5 users'}
                        </div>
                    </div>

                    <div style={{
                        marginLeft: 'auto',
                        padding: '8px 16px',
                        background: isPaidPlan ? colors.green[100] : colors.gray[100],
                        color: isPaidPlan ? colors.green[700] : colors.gray[700],
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                    }}>
                        {subscription?.status || 'Free'}
                    </div>
                </div>

                {/* User Limit Info */}
                <div style={{
                    padding: '16px',
                    background: colors.gray[50],
                    borderRadius: '12px',
                    marginBottom: '32px',
                }}>
                    <div style={{ fontSize: '14px', color: colors.gray[600], marginBottom: '8px' }}>
                        User Limit
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: colors.gray[900] }}>
                        {subscription?.userLimit || 5} users
                    </div>
                </div>

                {/* Action Buttons */}
                {isFreePlan ? (
                    <div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            marginBottom: '16px',
                            color: colors.gray[900],
                        }}>
                            Upgrade Your Plan
                        </h3>
                        <p style={{
                            fontSize: '16px',
                            color: colors.gray[600],
                            marginBottom: '24px',
                        }}>
                            Need more than 5 users? Choose a billing period to upgrade:
                        </p>

                        <div style={{ display: 'flex', gap: '16px' }}>
                            <button
                                onClick={() => handleUpgrade('monthly')}
                                disabled={upgrading}
                                style={{
                                    flex: 1,
                                    padding: '16px 32px',
                                    background: colors.white,
                                    border: `2px solid ${colors.landing.coral}`,
                                    borderRadius: '12px',
                                    color: colors.landing.coral,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    cursor: upgrading ? 'not-allowed' : 'pointer',
                                    opacity: upgrading ? 0.6 : 1,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseOver={(e) => {
                                    if (!upgrading) {
                                        e.target.style.background = colors.landing.coral;
                                        e.target.style.color = colors.white;
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!upgrading) {
                                        e.target.style.background = colors.white;
                                        e.target.style.color = colors.landing.coral;
                                    }
                                }}
                            >
                                Upgrade - Monthly ($8/user/mo)
                            </button>

                            <button
                                onClick={() => handleUpgrade('annual')}
                                disabled={upgrading}
                                style={{
                                    flex: 1,
                                    padding: '16px 32px',
                                    background: colors.gradients.coral,
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: colors.white,
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    cursor: upgrading ? 'not-allowed' : 'pointer',
                                    opacity: upgrading ? 0.6 : 1,
                                    boxShadow: shadows.coralMd,
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                }}
                                onMouseOver={(e) => {
                                    if (!upgrading) {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = shadows.coralLg;
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!upgrading) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = shadows.coralMd;
                                    }
                                }}
                            >
                                Upgrade - Annual ($80/user/yr)
                                <span style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    background: colors.yellow[400],
                                    color: colors.gray[900],
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                }}>
                                    SAVE 17%
                                </span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <button
                            onClick={handleManageBilling}
                            disabled={upgrading}
                            style={{
                                padding: '16px 32px',
                                background: colors.gradients.coral,
                                border: 'none',
                                borderRadius: '12px',
                                color: colors.white,
                                fontSize: '18px',
                                fontWeight: 700,
                                cursor: upgrading ? 'not-allowed' : 'pointer',
                                opacity: upgrading ? 0.6 : 1,
                                boxShadow: shadows.coralMd,
                                transition: 'all 0.2s ease',
                            }}
                            onMouseOver={(e) => {
                                if (!upgrading) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = shadows.coralLg;
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!upgrading) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = shadows.coralMd;
                                }
                            }}
                        >
                            Manage Billing & Payment Methods
                        </button>
                        <p style={{
                            fontSize: '14px',
                            color: colors.gray[600],
                            marginTop: '12px',
                        }}>
                            Update payment method, view invoices, or cancel subscription
                        </p>
                    </div>
                )}
            </div>

            {/* Pricing Info */}
            <div style={{
                background: colors.gray[50],
                borderRadius: '16px',
                padding: '32px',
                border: `1px solid ${colors.gray[200]}`,
            }}>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    marginBottom: '16px',
                    color: colors.gray[900],
                }}>
                    Pricing Information
                </h3>
                <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    fontSize: '15px',
                    color: colors.gray[700],
                    lineHeight: 1.8,
                }}>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Free:</strong> Up to 5 users, unlimited knowledge entries
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Pro:</strong> $8/user/month or $80/user/year (6-50 users)
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Enterprise:</strong> $399/month flat rate for unlimited users
                    </li>
                    <li style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${colors.gray[300]}` }}>
                        All paid plans include priority support, advanced integrations, and usage analytics
                    </li>
                </ul>
            </div>
        </div>
    );
}
